import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { authAxios, QueryStatus } from "../../../utils";
import { RootState } from "../../../redux/store";
import {
  AdvancedAnalyticsVehicles,
  AdvancedAnalyticsVehiclesApi,
  DriversHistory,
  MileageGapReportFileExportForDepartmentUserPayload,
  MonthlyMileageGapReportsListByDepartmentUserPayload,
  MonthlyReportApi,
  VehicleRestitutionPictures,
  VehicleSession,
  VehicleSessionApi,
} from "./fleetApi";
import type {
  GenerateMileageGapReportPayload,
  GetMileageGapReportFileByUuidResponse,
  GetMileageGapReportsResponse,
} from "@qivia/schemas-cards-mileage-gap-reports";
import {
  getAdditionalCostEndContract,
  getConsumedContractDurationMonth,
  getContractDurationMonth,
  getContractMensualMileage,
  getMensualMileage,
  getMileageDeviationPercentage,
  getMileageVariationEndContract,
  getMonthlyMileageDeviation,
  getProjectionEndOfContract,
  getRemainingContractDurationMonth,
} from "./lib";
import { capitalize } from "@qivia/ui";
import { PlanningsMonthlyRowsByCompanyResponse } from "@qivia/schemas-cards-drivers-plannings";
import axios from "axios";

export interface FleetState {
  advancedAnalyticVehiclesStatus: QueryStatus;
  driversHistoryStatus: QueryStatus;
  advancedAnalyticVehicles: AdvancedAnalyticsVehicles[];
  driversHistory: DriversHistory[];
  planningMonthlyRowsStatus: QueryStatus;
  planningMonthlyRows: PlanningsMonthlyRowsByCompanyResponse;
  monthlyReportsStatus: QueryStatus;
  monthlyReports: MonthlyReportApi[];
  generateMonthlyMileageGapReportStatus: QueryStatus;
  mileageGapReportFileExportStatus: QueryStatus;
  vehicleSessionsStatus: QueryStatus;
  vehicleSessions: VehicleSession[];
  restitutionInitialPicturesStatus: QueryStatus;
  restitutionCorrectivePicturesStatus: QueryStatus;
  restitutionPictures: Record<string, VehicleRestitutionPictures>;
}

const initialState: FleetState = {
  advancedAnalyticVehiclesStatus: "idle",
  driversHistoryStatus: "idle",
  advancedAnalyticVehicles: [],
  driversHistory: [],
  planningMonthlyRowsStatus: "idle",
  planningMonthlyRows: [],
  monthlyReportsStatus: "idle",
  monthlyReports: [],
  generateMonthlyMileageGapReportStatus: "idle",
  mileageGapReportFileExportStatus: "idle",
  vehicleSessionsStatus: "idle",
  vehicleSessions: [],
  restitutionInitialPicturesStatus: "idle",
  restitutionCorrectivePicturesStatus: "idle",
  restitutionPictures: {},
};

export const driversHistoryAsync = createAsyncThunk(
  "driversHistory/call",
  async (companyUuid: string, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    return (
      await axios.get<DriversHistory[]>(
        `drivers_history/company/${companyUuid}`,
      )
    ).data;
  },
);

export const driversHistoryByDepartmentAsync = createAsyncThunk(
  "driversHistoryByDepartment/call",
  async (departmentUuid: string) => {
    const axios = authAxios({ departmentUuid });
    return (
      await axios.get<DriversHistory[]>(
        `departments/drivers_history/department/${departmentUuid}`,
      )
    ).data;
  },
);

export const vehicleSessionsAsync = createAsyncThunk(
  "vehicleSessions/call",
  async (companyUuid: string) => {
    const axios = authAxios({ companyUuid });
    const response = await axios.get<VehicleSessionApi[]>(
      `sessions/company/${companyUuid}`,
    );

    return response.data.map((session) => {
      const initialRestitution = session.restitutions.find(
        (restitution) => restitution.declarationType === "INITIAL",
      );
      const correctiveRestitution = session.restitutions.find(
        (restitution) => restitution.declarationType === "CORRECTIVE",
      );
      const latestRestitution = correctiveRestitution ?? initialRestitution;

      return {
        uuid: session.uuid,
        driverUuid: session.driverInfo.uuid,
        vehicleUuid: session.vehicleInfo.uuid,
        startOfUsage: session.startOfUsage,
        endOfUsage: session.endOfUsage,
        registrationNumber: session.vehicleInfo.registrationNumber,
        reference: session.vehicleInfo.reference,
        department: session.vehicleInfo.department,
        departmentName: null,
        driverName: `${session.driverInfo.firstName} ${session.driverInfo.lastName}`,
        departureLocation: initialRestitution?.departureLocation ?? null,
        furthestDestination: initialRestitution?.furthestDestination ?? null,
        mileage: latestRestitution?.mileage ?? null,
        isCabinCleaned: latestRestitution?.isCabinCleaned ?? null,
        isTrunkCleaned: latestRestitution?.isTrunkCleaned ?? null,
        observations: latestRestitution?.observations ?? null,
        restitutions: session.restitutions,
      };
    });
  },
);

export const vehicleSessionsByDepartmentAsync = createAsyncThunk(
  "vehicleSessionsByDepartment/call",
  async (departmentUuid: string) => {
    const axios = authAxios({ departmentUuid });
    const response = await axios.get<VehicleSessionApi[]>(
      `departments/drivers_history/sessions/department/${departmentUuid}`,
    );

    return response.data.map((session) => {
      const initialRestitution = session.restitutions.find(
        (restitution) => restitution.declarationType === "INITIAL",
      );
      const correctiveRestitution = session.restitutions.find(
        (restitution) => restitution.declarationType === "CORRECTIVE",
      );
      const latestRestitution = correctiveRestitution ?? initialRestitution;

      return {
        uuid: session.uuid,
        driverUuid: session.driverInfo.uuid,
        vehicleUuid: session.vehicleInfo.uuid,
        startOfUsage: session.startOfUsage,
        endOfUsage: session.endOfUsage,
        registrationNumber: session.vehicleInfo.registrationNumber,
        reference: session.vehicleInfo.reference,
        department: session.vehicleInfo.department,
        departmentName: null,
        driverName: `${session.driverInfo.firstName} ${session.driverInfo.lastName}`,
        departureLocation: initialRestitution?.departureLocation ?? null,
        furthestDestination: initialRestitution?.furthestDestination ?? null,
        mileage: latestRestitution?.mileage ?? null,
        isCabinCleaned: latestRestitution?.isCabinCleaned ?? null,
        isTrunkCleaned: latestRestitution?.isTrunkCleaned ?? null,
        observations: latestRestitution?.observations ?? null,
        restitutions: session.restitutions,
      };
    });
  },
);

const restitutionPicturesThunk = (name: string) =>
  createAsyncThunk(name, async (restitutionUuid: string, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<VehicleRestitutionPictures>(
      `vehicle_restitution/${restitutionUuid}/pictures`,
    );

    thunkAPI.dispatch(
      fleetSlice.actions.setPicturesForRestitution({
        restitutionUuid,
        pictures: response.data,
      }),
    );
  });

export const restitutionInitialPicturesAsync = restitutionPicturesThunk(
  "restitutionInitialPictures/call",
);
export const restitutionCorrectivePicturesAsync = restitutionPicturesThunk(
  "restitutionCorrectivePictures/call",
);

export const planningMonthlyRowsAsync = createAsyncThunk(
  "planningMonthlyRows/call",
  async (companyUuid: string) => {
    const axios = authAxios({ companyUuid });
    return (
      await axios.get<PlanningsMonthlyRowsByCompanyResponse>(
        `driver_plannings/monthly_rows?companyUuid=${companyUuid}`,
      )
    ).data;
  },
);

export const monthlyMileageGapReportsListByUserAsync = createAsyncThunk(
  "monthlyMileageGapReportsListByUser/call",
  async (userUuid: string, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const { data } = await axios.get<GetMileageGapReportsResponse>(
      `mileage_gap_reports/list_by_user?userUuid=${userUuid}`,
    );
    return data;
  },
);

export const monthlyMileageGapReportsListByDepartmentUserAsync =
  createAsyncThunk(
    "monthlyMileageGapReportsListByDepartmentUser/call",
    async (payload: MonthlyMileageGapReportsListByDepartmentUserPayload) => {
      const axios = authAxios({ departmentUuid: payload.departmentUuid });
      const { data } = await axios.get<GetMileageGapReportsResponse>(
        `departments/mileage_gap_reports/list_by_user?userUuid=${payload.managerUuid}`,
      );
      return data;
    },
  );

export const generateMonthlyMileageGapReportAsync = createAsyncThunk(
  "generateMonthlyMileageGapReport/call",
  async (payload: GenerateMileageGapReportPayload, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.post("mileage_gap_reports", payload);
  },
);

const downloadFile = async (presignedUrl: string, fileName: string) => {
  if (presignedUrl && fileName) {
    const response = await axios({
      url: presignedUrl,
      method: "GET",
      responseType: "blob",
    });

    const a = document.createElement("a");
    a.download = fileName;
    const url = URL.createObjectURL(new Blob([response.data]));
    a.href = url;
    a.click();
    URL.revokeObjectURL(url);
  }
};

export const mileageGapReportFileExportAsync = createAsyncThunk(
  "mileageGapReportFileExport/call",
  async (reportFileUuid: string, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<GetMileageGapReportFileByUuidResponse>(
      `mileage_gap_report_file/${reportFileUuid}/export`,
    );

    await downloadFile(response.data.url, response.data.fileName);
  },
);

export const mileageGapReportFileExportForDepartmentUserAsync =
  createAsyncThunk(
    "mileageGapReportFileExportForDepartmentUser/call",
    async (payload: MileageGapReportFileExportForDepartmentUserPayload) => {
      const axios = authAxios({ departmentUuid: payload.departmentUuid });
      const response = await axios.get<GetMileageGapReportFileByUuidResponse>(
        `departments/mileage_gap_report_file/${payload.reportFileUuid}/export`,
      );

      await downloadFile(response.data.url, response.data.fileName);
    },
  );

export const advancedAnalyticVehiclesAsync = createAsyncThunk(
  "advancednalyticVehicles/call",
  async (companyUuid: string, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<AdvancedAnalyticsVehiclesApi[]>(
      `vehicles_advanced_analytics/company/${companyUuid}`,
    );

    return response.data.map((d) => {
      const additionalCostPerKm =
        d.additionalCostPerKm !== null
          ? Number((d.additionalCostPerKm / 100).toFixed(2))
          : null;

      //DATE CALCULATION
      const contractDurationMonth = getContractDurationMonth(
        d.contractStartDate,
        d.contractEndDate,
      );

      const consumedContractDuration = getConsumedContractDurationMonth(
        d.contractStartDate,
        contractDurationMonth,
      );

      const remainingContractDuration = getRemainingContractDurationMonth(
        contractDurationMonth,
        consumedContractDuration,
      );

      //MILEAGE CALCULATION
      const mensualContractMileage = getContractMensualMileage(
        d.contractMileage,
        contractDurationMonth,
      );

      const mensualMileage = getMensualMileage(d.mileage, d.contractStartDate);

      const projectionEndOfContract = getProjectionEndOfContract(
        mensualMileage,
        contractDurationMonth,
      );
      const mileageVariationEndContract = getMileageVariationEndContract(
        d.contractMileage,
        projectionEndOfContract,
      );
      const additionalCostEndContract = getAdditionalCostEndContract(
        mileageVariationEndContract,
        additionalCostPerKm,
      );

      const monthlyMileageKmVariation = getMonthlyMileageDeviation(
        mensualMileage,
        mensualContractMileage,
      );
      const mileageDeviationPercentage = getMileageDeviationPercentage(
        mensualMileage,
        mensualContractMileage,
      );
      return {
        ...d,
        contractDurationMonth,
        mensualContractMileage,
        mensualMileage,
        mileageDeviation:
          mileageDeviationPercentage && mileageDeviationPercentage > 10
            ? ("OVERROLLING" as const)
            : mileageDeviationPercentage && mileageDeviationPercentage < -10
              ? ("UNDERROLLING" as const)
              : mileageDeviationPercentage
                ? ("CORRECT" as const)
                : null,
        monthlyMileageKmVariation,
        mileageDeviationPercentage,
        projectionEndOfContract,
        mileageVariationEndContract,
        additionalCostEndContract,
        additionalCostPerKm,
        consumedContractDuration,
        remainingContractDuration,
        contractStartDate: d.contractStartDate,
        contractEndDate: d.contractEndDate,
        driverName: d.driverName && capitalize(d.driverName),
        reference: d.reference?.toLocaleUpperCase() ?? null,
      };
    });
  },
);

export const advancedAnalyticVehiclesByDepartmentAsync = createAsyncThunk(
  "advancednalyticVehiclesByDepartment/call",
  async (departmentUuid: string) => {
    const axios = authAxios({ departmentUuid });
    const response = await axios.get<AdvancedAnalyticsVehiclesApi[]>(
      `departments/vehicles/advanced_analytics/department/${departmentUuid}`,
    );

    return response.data.map((d) => {
      const additionalCostPerKm =
        d.additionalCostPerKm !== null
          ? Number((d.additionalCostPerKm / 100).toFixed(2))
          : null;

      //DATE CALCULATION
      const contractDurationMonth = getContractDurationMonth(
        d.contractStartDate,
        d.contractEndDate,
      );

      const consumedContractDuration = getConsumedContractDurationMonth(
        d.contractStartDate,
        contractDurationMonth,
      );

      const remainingContractDuration = getRemainingContractDurationMonth(
        contractDurationMonth,
        consumedContractDuration,
      );

      //MILEAGE CALCULATION
      const mensualContractMileage = getContractMensualMileage(
        d.contractMileage,
        contractDurationMonth,
      );

      const mensualMileage = getMensualMileage(d.mileage, d.contractStartDate);

      const projectionEndOfContract = getProjectionEndOfContract(
        mensualMileage,
        contractDurationMonth,
      );
      const mileageVariationEndContract = getMileageVariationEndContract(
        d.contractMileage,
        projectionEndOfContract,
      );
      const additionalCostEndContract = getAdditionalCostEndContract(
        mileageVariationEndContract,
        additionalCostPerKm,
      );

      const monthlyMileageKmVariation = getMonthlyMileageDeviation(
        mensualMileage,
        mensualContractMileage,
      );
      const mileageDeviationPercentage = getMileageDeviationPercentage(
        mensualMileage,
        mensualContractMileage,
      );
      return {
        ...d,
        contractDurationMonth,
        mensualContractMileage,
        mensualMileage,
        mileageDeviation:
          mileageDeviationPercentage && mileageDeviationPercentage > 10
            ? ("OVERROLLING" as const)
            : mileageDeviationPercentage && mileageDeviationPercentage < -10
              ? ("UNDERROLLING" as const)
              : mileageDeviationPercentage
                ? ("CORRECT" as const)
                : null,
        monthlyMileageKmVariation,
        mileageDeviationPercentage,
        projectionEndOfContract,
        mileageVariationEndContract,
        additionalCostEndContract,
        additionalCostPerKm,
        consumedContractDuration,
        remainingContractDuration,
        contractStartDate: d.contractStartDate,
        contractEndDate: d.contractEndDate,
        driverName: d.driverName && capitalize(d.driverName),
        reference: d.reference?.toLocaleUpperCase() ?? null,
      };
    });
  },
);

export const fleetSlice = createSlice({
  name: "fleet",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    resetFleetListStatus(state) {
      state.advancedAnalyticVehiclesStatus = "idle";
    },
    resetDriversHistoryListStatus(state) {
      state.driversHistoryStatus = "idle";
    },
    resetGenerateMonthlyMileageGapReportStatus(state) {
      state.generateMonthlyMileageGapReportStatus = "idle";
    },
    resetMileageGapReportFileExportStatus(state) {
      state.mileageGapReportFileExportStatus = "idle";
    },
    resetVehicleSessionsListStatus(state) {
      state.vehicleSessionsStatus = "idle";
    },
    resetRestitutionPicturesStatus(state) {
      state.restitutionInitialPicturesStatus = "idle";
      state.restitutionCorrectivePicturesStatus = "idle";
      state.restitutionPictures = {};
    },
    setPicturesForRestitution(
      state,
      action: PayloadAction<{
        restitutionUuid: string;
        pictures: VehicleRestitutionPictures;
      }>,
    ) {
      state.restitutionPictures[action.payload.restitutionUuid] =
        action.payload.pictures;
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(advancedAnalyticVehiclesAsync.pending, (state) => {
        state.advancedAnalyticVehiclesStatus = "processing";
      })
      .addCase(advancedAnalyticVehiclesAsync.fulfilled, (state, action) => {
        state.advancedAnalyticVehicles = action.payload;
        state.advancedAnalyticVehiclesStatus = "success";
      })
      .addCase(advancedAnalyticVehiclesAsync.rejected, (state) => {
        state.advancedAnalyticVehiclesStatus = "failed";
      })
      .addCase(advancedAnalyticVehiclesByDepartmentAsync.pending, (state) => {
        state.advancedAnalyticVehiclesStatus = "processing";
      })
      .addCase(
        advancedAnalyticVehiclesByDepartmentAsync.fulfilled,
        (state, action) => {
          state.advancedAnalyticVehicles = action.payload;
          state.advancedAnalyticVehiclesStatus = "success";
        },
      )
      .addCase(advancedAnalyticVehiclesByDepartmentAsync.rejected, (state) => {
        state.advancedAnalyticVehiclesStatus = "failed";
      })
      .addCase(driversHistoryAsync.pending, (state) => {
        state.driversHistoryStatus = "processing";
      })
      .addCase(driversHistoryAsync.fulfilled, (state, action) => {
        state.driversHistory = action.payload;
        state.driversHistoryStatus = "success";
      })
      .addCase(driversHistoryAsync.rejected, (state) => {
        state.driversHistoryStatus = "failed";
      })
      .addCase(driversHistoryByDepartmentAsync.pending, (state) => {
        state.driversHistoryStatus = "processing";
      })
      .addCase(driversHistoryByDepartmentAsync.fulfilled, (state, action) => {
        state.driversHistory = action.payload;
        state.driversHistoryStatus = "success";
      })
      .addCase(driversHistoryByDepartmentAsync.rejected, (state) => {
        state.driversHistoryStatus = "failed";
      })
      .addCase(planningMonthlyRowsAsync.pending, (state) => {
        state.planningMonthlyRowsStatus = "processing";
      })
      .addCase(planningMonthlyRowsAsync.fulfilled, (state, action) => {
        state.planningMonthlyRows = action.payload;
        state.planningMonthlyRowsStatus = "success";
      })
      .addCase(planningMonthlyRowsAsync.rejected, (state) => {
        state.planningMonthlyRowsStatus = "failed";
      })
      .addCase(monthlyMileageGapReportsListByUserAsync.pending, (state) => {
        state.monthlyReportsStatus = "processing";
      })
      .addCase(
        monthlyMileageGapReportsListByUserAsync.fulfilled,
        (state, action) => {
          state.monthlyReports = action.payload;
          state.monthlyReportsStatus = "success";
        },
      )
      .addCase(monthlyMileageGapReportsListByUserAsync.rejected, (state) => {
        state.monthlyReportsStatus = "failed";
      })
      .addCase(
        monthlyMileageGapReportsListByDepartmentUserAsync.pending,
        (state) => {
          state.monthlyReportsStatus = "processing";
        },
      )
      .addCase(
        monthlyMileageGapReportsListByDepartmentUserAsync.fulfilled,
        (state, action) => {
          state.monthlyReports = action.payload;
          state.monthlyReportsStatus = "success";
        },
      )
      .addCase(
        monthlyMileageGapReportsListByDepartmentUserAsync.rejected,
        (state) => {
          state.monthlyReportsStatus = "failed";
        },
      )
      .addCase(generateMonthlyMileageGapReportAsync.pending, (state) => {
        state.generateMonthlyMileageGapReportStatus = "processing";
      })
      .addCase(generateMonthlyMileageGapReportAsync.fulfilled, (state) => {
        state.generateMonthlyMileageGapReportStatus = "success";
      })
      .addCase(generateMonthlyMileageGapReportAsync.rejected, (state) => {
        state.generateMonthlyMileageGapReportStatus = "failed";
      })
      .addCase(
        mileageGapReportFileExportForDepartmentUserAsync.pending,
        (state) => {
          state.mileageGapReportFileExportStatus = "processing";
        },
      )
      .addCase(
        mileageGapReportFileExportForDepartmentUserAsync.fulfilled,
        (state) => {
          state.mileageGapReportFileExportStatus = "success";
        },
      )
      .addCase(
        mileageGapReportFileExportForDepartmentUserAsync.rejected,
        (state) => {
          state.mileageGapReportFileExportStatus = "failed";
        },
      )
      .addCase(mileageGapReportFileExportAsync.pending, (state) => {
        state.mileageGapReportFileExportStatus = "processing";
      })
      .addCase(mileageGapReportFileExportAsync.fulfilled, (state) => {
        state.mileageGapReportFileExportStatus = "success";
      })
      .addCase(mileageGapReportFileExportAsync.rejected, (state) => {
        state.mileageGapReportFileExportStatus = "failed";
      })
      .addCase(vehicleSessionsAsync.pending, (state) => {
        state.vehicleSessionsStatus = "processing";
      })
      .addCase(vehicleSessionsAsync.fulfilled, (state, action) => {
        state.vehicleSessions = action.payload;
        state.vehicleSessionsStatus = "success";
      })
      .addCase(vehicleSessionsAsync.rejected, (state) => {
        state.vehicleSessionsStatus = "failed";
      })
      .addCase(vehicleSessionsByDepartmentAsync.pending, (state) => {
        state.vehicleSessionsStatus = "processing";
      })
      .addCase(vehicleSessionsByDepartmentAsync.fulfilled, (state, action) => {
        state.vehicleSessions = action.payload;
        state.vehicleSessionsStatus = "success";
      })
      .addCase(vehicleSessionsByDepartmentAsync.rejected, (state) => {
        state.vehicleSessionsStatus = "failed";
      })
      .addCase(restitutionInitialPicturesAsync.pending, (state) => {
        state.restitutionInitialPicturesStatus = "processing";
      })
      .addCase(restitutionInitialPicturesAsync.fulfilled, (state) => {
        state.restitutionInitialPicturesStatus = "idle";
      })
      .addCase(restitutionInitialPicturesAsync.rejected, (state) => {
        state.restitutionInitialPicturesStatus = "failed";
      })
      .addCase(restitutionCorrectivePicturesAsync.pending, (state) => {
        state.restitutionCorrectivePicturesStatus = "processing";
      })
      .addCase(restitutionCorrectivePicturesAsync.fulfilled, (state) => {
        state.restitutionCorrectivePicturesStatus = "idle";
      })
      .addCase(restitutionCorrectivePicturesAsync.rejected, (state) => {
        state.restitutionCorrectivePicturesStatus = "failed";
      });
  },
});

export const selectAdvancedAnalyticsVehiclesList = (state: RootState) =>
  state.fleet.advancedAnalyticVehicles;
export const selectAdvancedAnalyticsVehiclesListStatus = (state: RootState) =>
  state.fleet.advancedAnalyticVehiclesStatus;
export const selectDriversHistoryList = (state: RootState) =>
  state.fleet.driversHistory;
export const selectDriversHistoryListStatus = (state: RootState) =>
  state.fleet.driversHistoryStatus;
export const selectPlanningMonthlyRows = (state: RootState) =>
  state.fleet.planningMonthlyRows;
export const selectPlanningMonthlyRowsStatus = (state: RootState) =>
  state.fleet.planningMonthlyRowsStatus;
export const selectMonthlyReportsList = (state: RootState) =>
  state.fleet.monthlyReports;
export const selectMonthlyReportsListStatus = (state: RootState) =>
  state.fleet.monthlyReportsStatus;
export const selectGenerateMonthlyMileageGapReportStatus = (state: RootState) =>
  state.fleet.generateMonthlyMileageGapReportStatus;
export const selectMileageGapReportFileExportStatus = (state: RootState) =>
  state.fleet.mileageGapReportFileExportStatus;
export const selectVehicleSessionsList = (state: RootState) =>
  state.fleet.vehicleSessions;
export const selectVehicleSessionsListStatus = (state: RootState) =>
  state.fleet.vehicleSessionsStatus;
export const selectRestitutionInitialPicturesStatus = (state: RootState) =>
  state.fleet.restitutionInitialPicturesStatus;
export const selectRestitutionCorrectivePicturesStatus = (state: RootState) =>
  state.fleet.restitutionCorrectivePicturesStatus;
export const selectRestitutionPictures = (state: RootState) =>
  state.fleet.restitutionPictures;

export default fleetSlice.reducer;
